import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import pdfWorkerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

const MAX_PAGES = 48;

/**
 * رندر PDF با pdf.js روی canvas — روی موبایل جایگزین iframe می‌شود تا UI کروم (Open / uuid) دیده نشود.
 */
export default function PdfJsCanvasViewer({ fileUrl, onLoadError }) {
  const hostRef = useRef(null);
  const [phase, setPhase] = useState("loading");
  const onErrorRef = useRef(onLoadError);
  onErrorRef.current = onLoadError;

  useEffect(() => {
    const src = fileUrl ? String(fileUrl).trim() : "";
    if (!src || !/^blob:|^data:/i.test(src)) {
      setPhase("error");
      onErrorRef.current?.();
      return undefined;
    }

    const host = hostRef.current;
    if (!host) return undefined;

    let cancelled = false;

    const run = async () => {
      setPhase("loading");
      host.replaceChildren();

      const layout = () => {
        const w = host.getBoundingClientRect().width;
        return Math.max(w - 12, 240);
      };

      try {
        let cssWidth = layout();
        if (cssWidth < 50) {
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
          cssWidth = layout();
        }

        const loadingTask = pdfjsLib.getDocument({url: src});
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const pageCount = Math.min(pdf.numPages, MAX_PAGES);

        for (let i = 1; i <= pageCount; i += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const baseViewport = page.getViewport({scale: 1});
          const scale = cssWidth / baseViewport.width;
          const viewport = page.getViewport({scale});
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d", {alpha: false});
          const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          canvas.className = "mx-auto mb-2 block max-w-full rounded shadow-sm";
          const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null;
          const renderTask = page.render({canvasContext: ctx, viewport, transform});
          await renderTask.promise;
          host.appendChild(canvas);
        }

        if (!cancelled) setPhase("ready");
      } catch {
        if (!cancelled) {
          setPhase("error");
          onErrorRef.current?.();
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-auto bg-slate-50/80">
      <div ref={hostRef} className="min-h-[200px] w-full flex-1 px-1.5 py-2" />
      {phase === "loading" ? (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60">
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : null}
    </div>
  );
}
